import React, { useEffect, useState } from 'react';
import { useLocalize } from '@saviynt/common';
import {
  ApplicationIcon,
  Box,
  Button,
  FooterActionBar,
  Icon,
  InlineMessage,
  InputField,
  MenuInput,
  ModalIsland,
  Typography,
} from '@saviynt/design-system';
import PropTypes from 'prop-types';
import validator from 'validator';

import { SessionLauncherDetails } from '../../../models/PamModels';
import { msgs } from '../constants';

import './SessionTargetDetailsModalIsland.css';

const DEFAULT_PORT_NUMBER_FOR_WINDOWS = '3389';
const DEFAULT_PORT_NUMBER_FOR_LINUX = '22';
const CRITICAL_INLINE_MESSAGE_KINDS = {
  SERVER_TYPE: 'server',
  IP: 'ip',
  PORT: 'port',
};

const SERVER_TYPES = {
  WINDOWS: 'windows',
  LINUX: 'linux',
};

const SERVER_OPTIONS = [
  {
    label: 'Windows',
    value: SERVER_TYPES.WINDOWS,
    leadingComp: <ApplicationIcon logo='windows' size='xxSmall' />,
  },
  {
    label: 'Linux',
    value: SERVER_TYPES.LINUX,
    leadingComp: <ApplicationIcon logo='unix' size='xxSmall' />,
  },
];

function SessionTargetDetailsModalIsland({
  setSessionToLaunch,
  sessionDetailsForAd,
  isSessionTargetModalVisible,
  setIsSessionTargetModalVisible,
  sessionTargetDetailsModalContentRef,
  sectionRefIsScrolled,
}) {
  // TODO: Set to number before sending to sessionToLaunch???? Set the sessionToLaunch sort of like this => sessionToLaunch: {...state, ipValue, portValue }
  // Input Values
  const [serverType, setServerType] = useState('');
  const [isServerTypeCritical, setIsServerTypeCritical] = useState(false);
  const [ipValue, setIpValue] = useState('');
  const [portValue, setPortValue] = useState('');
  const [isSubmitReady, setIsSubmitReady] = useState(false);
  //   Critical
  const [isIPCritical, setIsIPCritical] = useState(false);
  const [isPortCritical, setIsPortCritical] = useState(false);
  const [options, setOptions] = useState(SERVER_OPTIONS);

  const localize = useLocalize();

  // IP Validation
  const isIPv4 = validator.isIP(ipValue, 4);
  const isIPv6 = validator.isIP(ipValue, 6);

  // DNS Validation
  // PAM-13470 (customer bug) examples: VT00CLM01, VT00CLM01.test.jse.co.za
  // generic examples: example.com, subdomain.example.com, my-site.co.uk, example.travel, xn--example-dk9c.com

  const isValidDNS = validator.isFQDN(ipValue, {
    require_tld: false,
  });

  const isIpValid = isIPv6 || isIPv4 || isValidDNS;
  // Port Validation
  const isPortValid = validator.isInt(portValue, { min: 0, max: 65353 });

  // Server Type validation
  const isServerTypeValid = !!serverType;

  useEffect(() => {
    setIsSubmitReady(isIpValid && isPortValid);
  }, [ipValue, portValue]);

  // Localization
  const SERVER_TYPE_LABEL = localize(msgs.pam.sessionLauncher.label.serverType);
  const SERVER_TEXT = localize(msgs.pam.sessionLauncher.label.ipInput);
  const PORT_NUMBER_TEXT = localize(msgs.pam.sessionLauncher.label.portInput);
  const SERVER_TYPE_PLACEHOLDER = localize(
    msgs.pam.sessionLauncher.placeholder.serverType
  );

  const IP_PLACEHOLDER_TEXT = localize(
    msgs.pam.sessionLauncher.placeholder.ipInput
  );
  const PORT_INPUT_PLACEHOLDER_TEXT = localize(
    msgs.pam.sessionLauncher.placeholder.portInput
  );
  const CANCEL_TEXT = localize(msgs.pam.sessionLauncher.buttons.cancel);
  const LAUNCH_TEXT = localize(msgs.pam.sessionLauncher.buttons.launch);
  const LAUNCH_SESSION_TEXT = localize(
    msgs.pam.sessionLauncher.heading.detailsHeading
  );
  const MODAL_ISLAND_SUBHEADING_TEXT = localize(
    msgs.pam.sessionLauncher.heading.detailsSubheading
  );
  const SECONDARY_IP_CRIT_TEXT = localize(
    msgs.pam.sessionLauncher.critical.ipHelperText
  );
  const SECONDARY_PORT_CRIT_TEXT = localize(
    msgs.pam.sessionLauncher.critical.portHelperText
  );

  // Form handler functions
  const handleModalClose = () => {
    setIpValue('');
    setPortValue(DEFAULT_PORT_NUMBER_FOR_WINDOWS);
    setIsSessionTargetModalVisible(false);
  };

  const handleLaunchClick = () => {
    if (isSubmitReady) {
      setSessionToLaunch({
        ...sessionDetailsForAd,
        ipValue,
        portValue,
        serverType,
      });
      handleModalClose();
    } else if (isServerTypeValid) {
      if (!isIpValid) setIsIPCritical(true);
      if (!isPortValid) setIsPortCritical(true);
    } else {
      setIsServerTypeCritical(true);
    }
  };

  // Inline content is Based on input kind and state
  const inputCriticalHelperText = (kind) => {
    if (kind === CRITICAL_INLINE_MESSAGE_KINDS.IP && ipValue?.length !== 0) {
      return (
        <InlineMessage
          text={SECONDARY_IP_CRIT_TEXT}
          colorTheme='critical'
          size='small'
          leftIcon={<Icon kind='AlertCriticalFilled' color='critical-700' />}
        />
      );
    }

    if (
      kind === CRITICAL_INLINE_MESSAGE_KINDS.PORT &&
      portValue?.length !== 0
    ) {
      return (
        <InlineMessage
          text={SECONDARY_PORT_CRIT_TEXT}
          colorTheme='critical'
          size='small'
          leftIcon={<Icon kind='AlertCriticalFilled' color='critical-700' />}
        />
      );
    }

    if (
      kind === CRITICAL_INLINE_MESSAGE_KINDS.SERVER_TYPE &&
      !isServerTypeValid
    ) {
      return <InlineMessage kind='missingRequiredField' size='small' />;
    }

    return <InlineMessage kind='missingRequiredField' size='small' />;
  };

  const handleOnChange = (value) => {
    setServerType(value);
    setOptions((currentOptions) =>
      currentOptions.map((option) => ({
        ...option,
        isChecked: value && option.value === value,
      }))
    );
    setIsServerTypeCritical(false);

    if (value === SERVER_TYPES.WINDOWS) {
      setPortValue(DEFAULT_PORT_NUMBER_FOR_WINDOWS);
    } else if (value === SERVER_TYPES.LINUX) {
      setPortValue(DEFAULT_PORT_NUMBER_FOR_LINUX);
    } else {
      setIpValue('');
      setPortValue('');
      setIsIPCritical(false);
      setIsPortCritical(false);
    }
  };

  // Content nodes
  const getModalIslandContent = () => (
    <Box className='SessionTargetDetailsModalIsland-ModalIslandContent'>
      <MenuInput
        inputLabel={
          <Typography kind='label' htmlFor='Server Type'>
            {SERVER_TYPE_LABEL}
          </Typography>
        }
        placeholder={SERVER_TYPE_PLACEHOLDER}
        options={options}
        dropdownClassName='SessionTargetDetailsModalIsland-menuInput'
        onChange={(selectedOption) => handleOnChange(selectedOption.value)}
        isSearchDisabled
        isRequired
        isCritical={isServerTypeCritical}
        CriticalHelperText={inputCriticalHelperText(
          CRITICAL_INLINE_MESSAGE_KINDS.SERVER_TYPE
        )}
      />
      <InputField
        name='SessionTargetDetailsModalIsland-ipInput'
        placeholder={IP_PLACEHOLDER_TEXT}
        value={ipValue}
        setValue={setIpValue}
        label={
          <Typography
            kind='label'
            htmlFor='SessionTargetDetailsModalIsland-ipInput'>
            {SERVER_TEXT}
          </Typography>
        }
        CriticalHelperText={inputCriticalHelperText(
          CRITICAL_INLINE_MESSAGE_KINDS.IP
        )}
        isRequired
        isCritical={isIPCritical}
        isDisabled={!isServerTypeValid}
        setIsCritical={setIsIPCritical}
      />
      <InputField
        name='SessionTargetDetailsModalIsland-portInput'
        placeholder={PORT_INPUT_PLACEHOLDER_TEXT}
        value={portValue}
        setValue={setPortValue}
        label={
          <Typography
            kind='label'
            htmlFor='SessionTargetDetailsModalIsland-portInput'>
            {PORT_NUMBER_TEXT}
          </Typography>
        }
        CriticalHelperText={inputCriticalHelperText(
          CRITICAL_INLINE_MESSAGE_KINDS.PORT
        )}
        isRequired
        isCritical={isPortCritical}
        isDisabled={!isServerTypeValid}
        setIsCritical={setIsPortCritical}
        regexPattern='[0-9]*'
        inputmode='numeric'
      />
    </Box>
  );

  const modalIslandFooterActionBar = () => (
    <Box className='SessionTargetDetailsModalIsland-FooterActionBar'>
      <Button
        kind='ghost'
        size='medium'
        onClick={handleModalClose}
        dataTestId='footer-close-btn'>
        {CANCEL_TEXT}
      </Button>
      <Button
        kind={isSubmitReady ? 'filled' : 'outlined'}
        size='medium'
        onClick={handleLaunchClick}>
        {LAUNCH_TEXT}
      </Button>
    </Box>
  );

  return (
    <ModalIsland
      className='SessionTargetDetailsModalIsland'
      onClose={handleModalClose}
      isOpen={isSessionTargetModalVisible}
      title={LAUNCH_SESSION_TEXT}
      subtitle={MODAL_ISLAND_SUBHEADING_TEXT}
      contentBody={getModalIslandContent()}
      sectionRef={sessionTargetDetailsModalContentRef}
      FooterActionBarComp={
        <FooterActionBar size='large' isScrolled={sectionRefIsScrolled}>
          {modalIslandFooterActionBar()}
        </FooterActionBar>
      }
    />
  );
}

SessionTargetDetailsModalIsland.propTypes = {
  sessionDetailsForAd: PropTypes.shape(SessionLauncherDetails).isRequired,
  isSessionTargetModalVisible: PropTypes.bool.isRequired,
  setIsSessionTargetModalVisible: PropTypes.func.isRequired,
  sessionTargetDetailsModalContentRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]).isRequired,
  sectionRefIsScrolled: PropTypes.bool.isRequired,
  setSessionToLaunch: PropTypes.func.isRequired,
};

export default SessionTargetDetailsModalIsland;
